import { useState, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Loader2, Phone, ShieldCheck, Send, CheckCircle2, CalendarIcon, Clock, AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { createBooking } from "@/lib/api";
import { COMPANY } from "@/lib/data";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  pickup_date: "",
  pickup_time: "",
  pickup_address: "",
  destination_address: "",
  height: "",
  weight: "",
  additional_attendants: "0",
  trip_type: "one_way",
  transportation_type: "wheelchair",
  payment_method: "credit_card",
  notes: "",
  recurring: false,
  recurring_days: [],
  recurring_end_date: "",
};

const WEEK_DAYS = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

const RECURRING_PRESETS = [
  { label: "Mon / Wed / Fri", days: ["monday", "wednesday", "friday"] },
  { label: "Tue / Thu", days: ["tuesday", "thursday"] },
  { label: "Every weekday", days: ["monday", "tuesday", "wednesday", "thursday", "friday"] },
  { label: "Weekly (same day)", days: [] }, // will use pickup_date's weekday
];

// Generate 30-minute time slots from 5:00 AM to 9:30 PM
const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 5; h <= 21; h++) {
    for (let m of [0, 30]) {
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? "AM" : "PM";
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const label = `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
      out.push({ value, label });
    }
  }
  return out;
})();

const REQUIRED_FIELDS = {
  full_name: "Please enter your full name",
  email: "Please enter your email address",
  phone: "Please enter your phone number",
  pickup_date: "Please choose a pickup date",
  pickup_time: "Please choose a pickup time",
  pickup_address: "Please enter the pickup address",
  destination_address: "Please enter the destination address",
};

const FIELD_REFS_KEYS = Object.keys(REQUIRED_FIELDS);

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const refs = useRef({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    for (const [k, msg] of Object.entries(REQUIRED_FIELDS)) {
      if (!form[k] || !String(form[k]).trim()) next[k] = msg;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Please enter a valid email address";
    }
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) {
      next.phone = "Please enter a 10-digit phone number";
    }
    setErrors(next);
    return next;
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      const firstKey = FIELD_REFS_KEYS.find((k) => next[k]);
      const node = refs.current[firstKey];
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          if (typeof node.focus === "function") node.focus();
        }, 350);
      }
      const count = Object.keys(next).length;
      toast.error(`Please fix ${count} required field${count > 1 ? "s" : ""} below.`);
      return;
    }

    setLoading(true);
    try {
      await createBooking(form);
      toast.success("Transportation request submitted! Our team will contact you to confirm.");
      setSubmitted(true);
      setForm(initial);
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="bg-white rounded-3xl p-10 md:p-14 border border-teal-100 text-center shadow-xl">
        <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-teal-600" />
        </div>
        <h3 className="font-heading text-3xl font-semibold text-navy mt-6">Request Received</h3>
        <p className="text-navy/70 mt-3 max-w-md mx-auto text-lg">
          Thank you for choosing Comfort Care. Our scheduling team will contact you shortly to confirm your transportation,
          pricing, and pickup details.
        </p>
        <p className="text-navy/60 mt-3 text-base">
          Need to talk now? <a href={`tel:${COMPANY.phoneRaw}`} className="text-teal-600 font-semibold underline">Call {COMPANY.phone}</a>
        </p>
        <button onClick={() => setSubmitted(false)} className="btn-outline mt-8" data-testid="booking-submit-another">
          Submit another request
        </button>
      </div>
    );
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form onSubmit={submit} noValidate data-testid="booking-form" className="bg-white rounded-3xl p-7 md:p-10 lg:p-12 border border-gray-100 shadow-[0_30px_80px_-30px_rgba(10,25,47,0.2)]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-7 border-b border-gray-100">
        <div>
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy">Request Medical Transportation</h3>
          <p className="text-navy/60 mt-2 text-base">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. Our team confirms every booking by phone.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-500/10 px-4 py-2 rounded-full shrink-0">
          <ShieldCheck className="w-4 h-4" /> Secure submission
        </div>
      </div>

      {hasErrors && (
        <div data-testid="booking-error-banner" className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-red-900 font-semibold">Please complete the highlighted fields below.</div>
            <div className="text-red-700 text-sm mt-1">{Object.keys(errors).length} field{Object.keys(errors).length > 1 ? "s" : ""} need{Object.keys(errors).length > 1 ? "" : "s"} your attention.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5 mt-8">
        <Field label="Full Name" required error={errors.full_name}>
          <Input
            ref={(el) => (refs.current.full_name = el)}
            data-testid="booking-input-name"
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            placeholder="Jane Doe"
            className={cls("h-12 text-base", errors.full_name)}
          />
        </Field>
        <Field label="Email Address" required error={errors.email}>
          <Input
            ref={(el) => (refs.current.email = el)}
            data-testid="booking-input-email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="jane@example.com"
            className={cls("h-12 text-base", errors.email)}
          />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <Input
            ref={(el) => (refs.current.phone = el)}
            data-testid="booking-input-phone"
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="(571) 689-4470"
            className={cls("h-12 text-base", errors.phone)}
          />
        </Field>
        <Field label="Additional Attendants">
          <Input
            data-testid="booking-input-attendants"
            value={form.additional_attendants}
            onChange={(e) => set("additional_attendants", e.target.value)}
            placeholder="0"
            className="h-12 text-base"
          />
        </Field>

        {/* Date picker with shadcn Calendar */}
        <Field label="Pickup Date" required error={errors.pickup_date}>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button
                ref={(el) => (refs.current.pickup_date = el)}
                type="button"
                data-testid="booking-input-date"
                className={cls(
                  "h-12 w-full inline-flex items-center justify-between gap-2 rounded-md border px-4 text-base text-left bg-white hover:border-teal-400 transition",
                  errors.pickup_date,
                  !form.pickup_date && "text-navy/45"
                )}
              >
                <span className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-teal-500" />
                  {form.pickup_date ? format(new Date(form.pickup_date + "T00:00:00"), "EEEE, MMMM d, yyyy") : "Choose a date"}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.pickup_date ? new Date(form.pickup_date + "T00:00:00") : undefined}
                onSelect={(d) => {
                  if (d) {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    set("pickup_date", `${y}-${m}-${day}`);
                    setDateOpen(false);
                  }
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </Field>

        {/* Time slot picker */}
        <Field label="Pickup Time" required error={errors.pickup_time}>
          <Select value={form.pickup_time} onValueChange={(v) => set("pickup_time", v)}>
            <SelectTrigger
              ref={(el) => (refs.current.pickup_time = el)}
              data-testid="booking-input-time"
              className={cls("h-12 text-base", errors.pickup_time)}
            >
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-500" />
                <SelectValue placeholder="Choose a time" />
              </span>
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {TIME_SLOTS.map((t) => (
                <SelectItem key={t.value} value={t.value} className="text-base py-2.5">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Pickup Address" required error={errors.pickup_address} full>
          <AddressAutocomplete
            ref={(el) => (refs.current.pickup_address = el)}
            testId="booking-input-pickup"
            value={form.pickup_address}
            onChange={(v) => set("pickup_address", v)}
            placeholder="Start typing a street address, hospital, or facility name"
            error={errors.pickup_address}
          />
        </Field>
        <Field label="Destination Address" required error={errors.destination_address} full>
          <AddressAutocomplete
            ref={(el) => (refs.current.destination_address = el)}
            testId="booking-input-destination"
            value={form.destination_address}
            onChange={(v) => set("destination_address", v)}
            placeholder="Hospital, clinic, dialysis center, or street address"
            error={errors.destination_address}
          />
        </Field>

        <Field label="Height (optional)">
          <Input data-testid="booking-input-height" value={form.height} onChange={(e) => set("height", e.target.value)} placeholder='e.g. 5&apos;4"' className="h-12 text-base" />
        </Field>
        <Field label="Weight (optional)">
          <Input data-testid="booking-input-weight" value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 180 lbs" className="h-12 text-base" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-7 mt-9 pt-7 border-t border-gray-100">
        <div>
          <Label className="text-navy font-semibold text-sm">Trip Type</Label>
          <RadioGroup value={form.trip_type} onValueChange={(v) => set("trip_type", v)} className="mt-3 space-y-2.5" data-testid="booking-trip-type">
            <RadioRow id="trip-one" value="one_way" label="One Way" current={form.trip_type} />
            <RadioRow id="trip-round" value="round_trip" label="Round Trip" current={form.trip_type} />
          </RadioGroup>
        </div>

        <div>
          <Label className="text-navy font-semibold text-sm">Transportation Type</Label>
          <Select value={form.transportation_type} onValueChange={(v) => set("transportation_type", v)}>
            <SelectTrigger className="mt-3 h-12 text-base rounded-xl border-gray-200 focus:ring-teal-500" data-testid="booking-transport-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="wheelchair" className="text-base py-2.5">Wheelchair</SelectItem>
              <SelectItem value="ambulatory" className="text-base py-2.5">Ambulatory</SelectItem>
              <SelectItem value="stretcher" className="text-base py-2.5">Stretcher</SelectItem>
              <SelectItem value="other" className="text-base py-2.5">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-navy font-semibold text-sm">Payment Method</Label>
          <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
            <SelectTrigger className="mt-3 h-12 text-base rounded-xl border-gray-200 focus:ring-teal-500" data-testid="booking-payment-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="credit_card" className="text-base py-2.5">Credit Card</SelectItem>
              <SelectItem value="cash" className="text-base py-2.5">Cash</SelectItem>
              <SelectItem value="other" className="text-base py-2.5">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-7">
        <Label className="text-navy font-semibold text-sm">Additional Notes</Label>
        <Textarea
          data-testid="booking-input-notes"
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={4}
          className="mt-2 text-base rounded-xl border-gray-200 focus:ring-teal-500"
          placeholder="Special instructions, accessibility notes, or appointment details"
        />
      </div>

      <div className="mt-5 flex items-start gap-3">
        <Checkbox
          id="recurring"
          data-testid="booking-input-recurring"
          checked={form.recurring}
          onCheckedChange={(v) => set("recurring", !!v)}
          className="mt-0.5"
        />
        <label htmlFor="recurring" className="text-base text-navy/80 leading-relaxed cursor-pointer">
          This is a <strong className="text-navy">recurring ride</strong> (dialysis, rehab, ongoing treatment). Set the days below and we'll schedule the whole series in one shot.
        </label>
      </div>

      {form.recurring && (
        <div data-testid="recurring-panel" className="mt-4 p-5 bg-teal-500/5 border border-teal-500/20 rounded-2xl">
          <Label className="text-navy font-semibold text-sm">Days of the week</Label>
          <p className="text-navy/55 text-xs mt-1 mb-3">Tap all days this ride repeats. Your first pickup date above will be part of the series.</p>
          <div className="flex flex-wrap gap-2">
            {WEEK_DAYS.map((d) => {
              const active = form.recurring_days.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  data-testid={`recurring-day-${d.value}`}
                  onClick={() => {
                    const next = active
                      ? form.recurring_days.filter((x) => x !== d.value)
                      : [...form.recurring_days, d.value];
                    set("recurring_days", next);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                    active
                      ? "bg-teal-500 text-white border-teal-500 shadow-md"
                      : "bg-white text-navy/70 border-gray-200 hover:border-teal-400"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <span className="text-xs text-navy/50 mr-1 self-center">Quick presets:</span>
            {RECURRING_PRESETS.filter((p) => p.days.length).map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => set("recurring_days", p.days)}
                className="text-xs px-3 py-1 rounded-full bg-white border border-gray-200 text-navy/70 hover:border-teal-400 hover:text-teal-700 transition"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <Label className="text-navy font-semibold text-sm">End date (optional)</Label>
            <p className="text-navy/55 text-xs mt-1 mb-2">Leave blank if this is an ongoing schedule. Our team will confirm the series length with you.</p>
            <Input
              type="date"
              value={form.recurring_end_date}
              onChange={(e) => set("recurring_end_date", e.target.value)}
              className="h-12 text-base max-w-xs"
              data-testid="recurring-end-date"
              min={form.pickup_date || undefined}
            />
          </div>

          {form.recurring_days.length > 0 && (
            <div className="mt-4 text-sm text-teal-800 bg-white rounded-lg p-3 border border-teal-500/30 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <strong>Series:</strong> Every {form.recurring_days.map((d) => d.slice(0, 3).replace(/^./, (c) => c.toUpperCase())).join(", ")}
                {form.recurring_end_date ? ` through ${form.recurring_end_date}` : " · ongoing until you tell us otherwise"}
              </span>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-navy/55 mt-6 leading-relaxed bg-sand p-4 rounded-xl">
        <strong className="text-navy">Please note:</strong> Cancellations must be made by 5:00 PM on the previous business day to avoid a cancellation fee.
        Reservations are confirmed only after all required transport details and a valid payment method are received.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <button type="submit" disabled={loading} data-testid="booking-submit" className="btn-primary flex-1 sm:flex-none disabled:opacity-60 !h-14 !text-base">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {loading ? "Submitting..." : "Submit Transportation Request"}
        </button>
        <a href={`tel:${COMPANY.phoneRaw}`} className="btn-outline !h-14 !text-base">
          <Phone className="w-5 h-5" /> Or call {COMPANY.phone}
        </a>
      </div>
    </form>
  );
}

function Field({ label, required, error, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-navy font-semibold text-sm flex items-center gap-1">
        {label}
        {required && <span className="text-red-500" aria-label="required">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-red-600 text-sm font-medium" role="alert">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

function RadioRow({ id, value, label, current }) {
  const active = current === value;
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition ${
        active ? "border-teal-500 bg-teal-500/5" : "border-gray-200 hover:border-teal-300"
      }`}
    >
      <RadioGroupItem id={id} value={value} />
      <span className="text-navy text-base font-medium">{label}</span>
    </label>
  );
}

// Helper: merge classNames + apply error border style
function cls(base, hasError, ...extra) {
  return [
    base,
    hasError
      ? "border-red-400 focus-visible:ring-red-400 focus-visible:border-red-500 bg-red-50/40"
      : "border-gray-200 focus-visible:ring-teal-500",
    ...extra.filter(Boolean),
  ]
    .filter(Boolean)
    .join(" ");
}
